import { useEffect, useRef, useState } from 'react';
import { Globe, X, Check } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LANGUAGES = [
  { code: 'en',    label: 'English',     native: 'English',    flag: 'US' },
  { code: 'es',    label: 'Spanish',     native: 'Español',    flag: 'ES' },
  { code: 'vi',    label: 'Vietnamese',  native: 'Tiếng Việt', flag: 'VN' },
  { code: 'fr',    label: 'French',      native: 'Français',   flag: 'FR' },
  { code: 'zh-CN', label: 'Mandarin',    native: '中文',        flag: 'CN' },
  { code: 'ar',    label: 'Arabic',      native: 'العربية',    flag: 'SA' },
  { code: 'pt',    label: 'Portuguese',  native: 'Português',  flag: 'PT' },
];

const STORAGE_KEY = 'necs_preferred_language';
const ALERT_DISMISSED_KEY = 'necs_lang_alert_dismissed';

// Set Google Translate cookie + drive the underlying widget select
function applyGoogleTranslate(langCode) {
  if (!langCode) return;
  // Set the googtrans cookie at document + parent domain (root) so it sticks
  const cookieValue = langCode === 'en' ? '/en/en' : `/en/${langCode}`;
  const host = window.location.hostname;
  const domains = [host, `.${host}`, host.split('.').slice(-2).join('.'), `.${host.split('.').slice(-2).join('.')}`];
  domains.forEach((d) => {
    document.cookie = `googtrans=${cookieValue};path=/;domain=${d}`;
    document.cookie = `googtrans=${cookieValue};path=/`;
  });

  // Drive the hidden Google Translate <select> if it has rendered
  const select = document.querySelector('select.goog-te-combo');
  if (select) {
    select.value = langCode === 'en' ? '' : langCode;
    select.dispatchEvent(new Event('change'));
  } else {
    // Widget not yet rendered → reload so the cookie is picked up
    window.location.reload();
  }
}

export default function LanguageSwitcher({ variant = 'dark', testIdPrefix = 'lang' }) {
  const initialLang = (typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY)) || 'en';
  const [current, setCurrent] = useState(initialLang);
  const [showAlert, setShowAlert] = useState(false);
  const appliedRef = useRef(false);

  // First-time alert
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!localStorage.getItem(ALERT_DISMISSED_KEY)) {
      setShowAlert(true);
    }
  }, []);

  // Re-apply saved language once the Google Translate widget has loaded
  useEffect(() => {
    if (current === 'en' || appliedRef.current) return;

    let attempts = 0;
    const interval = setInterval(() => {
      const select = document.querySelector('select.goog-te-combo');
      if (select) {
        select.value = current;
        select.dispatchEvent(new Event('change'));
        appliedRef.current = true;
        clearInterval(interval);
      } else if (++attempts > 30) {
        clearInterval(interval);
      }
    }, 300);
    return () => clearInterval(interval);
  }, [current]);

  const handleSelect = (code) => {
    setCurrent(code);
    localStorage.setItem(STORAGE_KEY, code);
    applyGoogleTranslate(code);

    // Best-effort: persist to backend so notifications go out in this language
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (backendUrl) {
        fetch(`${backendUrl}/api/auth/me/language`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ preferred_language: code }),
        }).catch(() => {});
      }
    } catch (_) { /* ignore – switcher must never block */ }
  };

  const dismissAlert = () => {
    localStorage.setItem(ALERT_DISMISSED_KEY, '1');
    setShowAlert(false);
  };

  const currentLang = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];
  const isDark = variant === 'dark';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            data-testid={`${testIdPrefix}-switcher-trigger`}
            translate="no"
            className={`notranslate gap-2 font-semibold ${
              isDark
                ? 'bg-[#3D3D3D] border-[#FFD60A]/40 text-[#FFD60A] hover:bg-[#FFD60A] hover:text-black'
                : 'bg-white border-[#FFD60A]/40 text-[#3D3D3D] hover:bg-[#FFD60A] hover:text-black'
            }`}
          >
            <Globe className="h-4 w-4" />
            <span className="hidden sm:inline">{currentLang.native}</span>
            <span className="sm:hidden">{currentLang.code.toUpperCase()}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="notranslate w-56 bg-[#2D2D2D] border-[#FFD60A]/30 text-white"
          translate="no"
        >
          <DropdownMenuLabel className="text-[#FFD60A] flex items-center gap-2">
            <Globe className="h-4 w-4" /> Language / Idioma
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-[#FFD60A]/20" />
          {LANGUAGES.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              data-testid={`${testIdPrefix}-option-${lang.code}`}
              className="cursor-pointer focus:bg-[#FFD60A]/15 focus:text-white flex items-center justify-between"
            >
              <span className="flex flex-col">
                <span className="font-semibold">{lang.native}</span>
                <span className="text-xs text-gray-400">{lang.label}</span>
              </span>
              {current === lang.code && <Check className="h-4 w-4 text-[#FFD60A]" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator className="bg-[#FFD60A]/20" />
          <div className="px-2 py-1.5 text-[10px] text-gray-400 leading-snug">
            Powered by Google Translate. Switch anytime.
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {showAlert && (
        <div
          data-testid={`${testIdPrefix}-alert-banner`}
          className="notranslate fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] max-w-md w-[calc(100%-2rem)] bg-[#FFD60A] text-black rounded-xl shadow-2xl border-2 border-black/10 px-4 py-3 flex items-start gap-3"
          translate="no"
        >
          <Globe className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm leading-snug">
            <strong>Language available.</strong> You can switch to Español, Tiếng Việt, Français, 中文, العربية or Português anytime — tap the globe in the top bar.
          </div>
          <button
            onClick={dismissAlert}
            data-testid={`${testIdPrefix}-alert-dismiss`}
            className="flex-shrink-0 hover:bg-black/10 rounded-full p-1"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </>
  );
}
